import { type AggResponse, fetchAgg } from '../../../common/utils/fetch-agg';
import { inMemoryCache } from '../../../utils/caching/in-memory';

export const focusAreasCache = inMemoryCache<AggResponse<MercuryFocusAreasResponse>>();

const GQL_MERCURY_FOCUS_AREAS_OPERATION_NAME = 'FocusAreas';
const mercuryFocusAreasQueryString = `query ${GQL_MERCURY_FOCUS_AREAS_OPERATION_NAME}($cloudId: ID!, $q: String, $first: Int, $sort: [MercuryFocusAreaSort]) {
  mercury {
    focusAreas(cloudId: $cloudId, q: $q, first: $first, sort: $sort) @optIn(to: "Mercury") {
      edges {
        node {
          id
          name
          status {
		    key            
          }
          health {
            key
          }
          focusAreaType {
		  	name
            hierarchyLevel
          }
		  owner {
            name
          }
        }
      }
    }
  }
}`;

export type MercuryFocusArea = {
	node?: {
		/** The id of the focus area. */
		id: string;
		/** The name of the focus area. */
		name: string;
		/** The status of the focus area. */
		status: {
			key: string;
		};
		/** The health of the focus area. */
		health?: {
			key: string;
		};
		/** The focus area type of the focus area. */
		focusAreaType: {
			name: string;
			hierarchyLevel: number;
		};
		/** The owner of the focus area. */
		owner?: {
			name: string;
		};
	};
};

export type MercuryFocusAreasResponse = {
	mercury: {
		focusAreas: {
			edges?: MercuryFocusArea[];
		};
	};
};

const MercuryFocusAreaSortField = {
	Budget: 'BUDGET',
	FocusAreaType: 'FOCUS_AREA_TYPE',
	HasParent: 'HAS_PARENT',
	HierarchyLevel: 'HIERARCHY_LEVEL',
	LastUpdated: 'LAST_UPDATED',
	Name: 'NAME',
	Spend: 'SPEND',
	Status: 'STATUS',
	TargetDate: 'TARGET_DATE',
	Watching: 'WATCHING',
};

const MercuryFocusAreaSortOrder = {
	ASC: 'ASC',
	DESC: 'DESC',
} as const;

type MercuryFocusAreaSort = {
	field: (typeof MercuryFocusAreaSortField)[keyof typeof MercuryFocusAreaSortField];
	order: (typeof MercuryFocusAreaSortOrder)[keyof typeof MercuryFocusAreaSortOrder];
};

export type MercuryFocusAreasQueryVariables = {
	cloudId: string;
	q: string;
	first: number;
	sort: MercuryFocusAreaSort[];
};

export const mercuryFocusAreasQuery = (
	cloudId: string,
	q: string,
	first = 20,
	sort = [
		{
			field: MercuryFocusAreaSortField.FocusAreaType,
			order: MercuryFocusAreaSortOrder['ASC'],
		},
		{
			field: MercuryFocusAreaSortField.Name,
			order: MercuryFocusAreaSortOrder['ASC'],
		},
	],
) => {
	return focusAreasCache.inMemoryDecorator(
		`${cloudId}-${q}-${first}-${JSON.stringify(sort)}`,
		async () => {
			if (!cloudId) {
				return {
					data: {
						mercury: {
							focusAreas: {
								edges: [],
							},
						},
					},
				};
			}

			const response = await fetchAgg<MercuryFocusAreasQueryVariables, MercuryFocusAreasResponse>({
				graphQuery: mercuryFocusAreasQueryString,
				variables: { cloudId, q, first, sort },
				operationName: GQL_MERCURY_FOCUS_AREAS_OPERATION_NAME,
			});

			const hasErrors = response.errors?.some((error) => error);

			if (hasErrors) {
				return Promise.reject(response);
			}
			return response;
		},
	);
};
